import { Component, Input, OnInit, ChangeDetectorRef, NgZone } from "@angular/core";
import { isEnabled } from "../../../environments/environment";
import { EventNotificationSettings } from "../../model/notifications/event-notification-settings";
import { AuthService } from "../../auth/auth.service";
import { EventNotificationService } from "../../data-access-layer/notification/event-notification.service";
import { Account } from "../../model/account/account";
import { AccountMember } from "../../model/member/account-member";
import { Dataset } from "../../model/dataset/dataset";
import { Constants, DistanceUnit } from "../../constants";
import { DatapointsPageStateService } from "../datapoints/datapoints-page-state.service";
import { DatasetGeometryType } from "../../model/dataset/dataset-geometry-type";
import { Functionalities } from "../../../environments/app-functionalities";
import { GroupService } from "../../data-access-layer/groups/group.service";
import { NotifService } from "../../core/notification/notif.service";
import { DatasetType } from "src/app/model/dataset/dataset-type";
import { DatasetStylingOptions } from "src/app/model/dataset/rendering/dataset-styling-options";
import { ThematicMapSettings } from "src/app/model/dataset/thematicMap/thematic-map-settings";
import { MembersService } from "../../data-access-layer/members/members.service";
import {
    MapMarkerVisualizationMode,
    ShowInMap,
} from "src/app/model/dataset/dataset-config-filter.enum";
import { MaptycsApplication } from "src/app/model/account/maptycs-application";
import { DatasetFieldSpecificType } from "src/app/model/dataset/dataset-field-specific.type";
import { DatasetFieldType } from "src/app/model/dataset/dataset-field-type";
import { LayerExposedService } from "src/app/account/layer-exposed/services/layer-exposed.service";
import { DatasetField } from "src/app/model/dataset/field/dataset-field";
import { ActivatedRoute } from "@angular/router";
import { LayerExposeField } from "src/app/account/layer-exposed/services/layer-expose-field";
import { WorkspaceItemService } from "../../data-access-layer/workspace-item/workspace-item.service";
import { WorkspaceItemType } from "../../model/workspace/workspace-item-type";
import { WorkspaceItem } from "../../model/workspace/workspace-item";
import { OverlaysService } from "../../data-access-layer/global-overlays/overlays.service";
import { decodeHtmlText } from "../../core/utils/util-master";

const IS_PART_OF = Functionalities.DATAPOINTS;

@Component({
    selector: "map-event-notifications",
    templateUrl: "./event-notifications.component.html",
    styleUrls: ["./event-notifications.component.scss"],
})
export class EventNotificationsComponent implements OnInit {
    @Input() account: Account;
    @Input() dataset: Dataset;
    @Input() groupsIds: number[];

    isEnabled = isEnabled(IS_PART_OF);
    notifications: EventNotificationSettings[] = [];
    eligibleOverlays: Dataset[] = [];
    currencies: string[];
    showPanel: boolean;
    notificationsLoaded = false;
    groups: any[] = [];
    private accountMember: AccountMember;
    layerExposeFields: LayerExposeField[] = [];
    formulas: WorkspaceItem[] = [];
    accountMembers: AccountMember[] = [];
    accountMemberEmails: string[] = [];

    private readonly requiredOverlayIds = [
        "aeris_earthquake_circle",
        "aeris_storm_beautified",
        "aeris_hail_storm",
    ];

    constructor(
        private readonly eventNotifService: EventNotificationService,
        private readonly datapointsPageStateService: DatapointsPageStateService,
        private readonly route: ActivatedRoute,
        private readonly authService: AuthService,
        private readonly changeDetector: ChangeDetectorRef,
        private readonly ngZone: NgZone,
        private readonly layerExposedService: LayerExposedService,
        private readonly groupService: GroupService,
        private readonly notifService: NotifService,
        private readonly workspaceItemService: WorkspaceItemService,
        private readonly overlayService: OverlaysService,
        private readonly membersService: MembersService
    ) {}

    ngOnInit() {
        this.groupService
            .fetchGroupsForMember(this.dataset.id, this.account.id)
            .subscribe((groups) => {
                this.ngZone.run(() => {
                    this.showPanel = groups.length !== 0;
                    this.groups = groups;
                    this.changeDetector.detectChanges();
                });
            });
        let principal = this.authService.getPrincipal();
        if (!principal.isSuperadmin) {
            this.accountMember = principal.membership.find(
                (member) => member.accountID === this.account.id
            );
            this.eventNotifService
                .getNotificationsForMember(this.accountMember.id)
                .subscribe((notifications) => {
                    this.ngZone.run(() => {
                        this.notifications = notifications;
                        this.notificationsLoaded = true;
                        this.changeDetector.detectChanges();
                    });
                });
        } else {
            this.notificationsLoaded = true;
        }
        this.fetchFormulas();
        this.fetchAccountOverlays();
        this.eventNotifService
            .getCurrencies()
            .subscribe((currencies) => (this.currencies = currencies));
        this.route.params.subscribe((params) => {
            const accountId = params["accountId"];
            // Use account ID from URL if available, otherwise fall back to this.account.id
            const effectiveAccountId = accountId ? +accountId : this.account.id;
            this.fetchLayerExposeData(effectiveAccountId);
            this.fetchAccountMembers(effectiveAccountId);
        });
    }

    fetchLayerExposeData(accountId: number): void {
        this.layerExposedService
            .getAppliedLayerExpose(accountId)
            .subscribe(
                (data) => {
                    if (Array.isArray(data) && data.length > 0) {
                        this.layerExposeFields = data.map((equation) => ({
                            id: equation.id,
                            accountId: equation.accountId,
                            name: equation.name,
                            baseValue: equation.baseValue,
                            layerValue: equation.layerValue,
                            attachmentPoint: equation.attachmentPoint,
                            isApplied: equation.isApplied,
                            participation: equation.participation,
                        }));
                    } else {
                        this.layerExposeFields = [];
                    }
                },
                (error) => {
                    console.error("Failed to fetch layer expose data", error);
                    this.layerExposeFields = [];
                }
            );
    }

    private fetchAccountOverlays(): void {
        // Fetch overlays for the current account using the overlays service
        this.overlayService.getAllAccountOverlays(this.account.id).subscribe(
            (overlays) => {
                if (overlays && overlays.length > 0) {
                    // Filter to only include overlays that match our required IDs
                    const filteredOverlays = overlays.filter((overlay) =>
                        this.requiredOverlayIds.includes(overlay.id)
                    ).map((overlay) => {
                        if(overlay.id === "aeris_earthquake_circle") {
                            overlay.name = "Active Earthquake";
                            return overlay;
                        }
                        if(overlay.id === "aeris_storm_beautified") {
                            overlay.name = "Active Tropical Storm";
                            return overlay;
                        }
                        if(overlay.id === "aeris_hail_storm") {
                            overlay.name = "Active Hail Storm";
                            return overlay;
                        }
                        if(overlay.id === "aeris_tornado_outlooks") {
                            overlay.name = "Active Tornado Storm";
                            return overlay;
                        }
                    }
                );

                    if (filteredOverlays.length > 0) {
                        // We found at least some of our required overlays
                        this.eligibleOverlays = filteredOverlays;
                    } else {
                        // None of our required overlays were found, fallback to hardcoded
                        this.addHardcodedOverlays();
                    }
                } else {
                    // No overlays returned from API, fallback to hardcoded
                    this.addHardcodedOverlays();
                }
            },
            (error) => {
                console.error("Error fetching account overlays:", error);
                // Error occurred, fallback to hardcoded overlays
                this.addHardcodedOverlays();
            }
        );
    }

    private addHardcodedOverlays(): void {
        // Clear any existing overlays first to ensure only hardcoded ones are shown
        this.eligibleOverlays = [];

        // Hardcoded overlays: Only these will be available
        const hardcodedOverlays: Dataset[] = [
            {
                id: "aeris_earthquake_circle",
                version: 1,
                name: "Active Earthquake",
                geometryType: DatasetGeometryType.COMPLEX,
                geometryDatasets: [],
                accountID: this.account.id,
                mainSummaryFieldIndex: null,
                fields: [
                    {
                        name: "magnitude",
                        displayName: "Magnitude",
                        type: DatasetFieldSpecificType.NUMBER_FIELD,
                        baseType: DatasetFieldType.NUMBER,
                        isSummary: true,
                        isGenerated: false,
                        isRequired: false,
                        tags: [],
                    },
                ],
                isGeoserver: false,
                geoWorkspace: "",
                isRefreshable: true,
                isLinkable: true,
                hasHazardHubEnabled: false,
                lastRefreshDate: Date.now(),
                overlayGroupId: 0,
                stylingOptions: {} as DatasetStylingOptions,
                thematicMapSettings: {} as ThematicMapSettings,
                markerVisualizationMode:
                    MapMarkerVisualizationMode.MARKER_PINS_ONLY,
                application: MaptycsApplication.LOCATIONS,
                isGeocoded: false,
                type: DatasetType.GLOBAL_OVERLAY,
                showIn: ShowInMap.Filter,
            },
            {
                id: "aeris_storm_beautified",
                version: 1,
                name: "Active Tropical Storm",
                geometryType: DatasetGeometryType.COMPLEX,
                geometryDatasets: [],
                accountID: this.account.id,
                mainSummaryFieldIndex: null,
                fields: [],
                isGeoserver: false,
                geoWorkspace: "",
                isRefreshable: true,
                isLinkable: true,
                hasHazardHubEnabled: false,
                lastRefreshDate: Date.now(),
                overlayGroupId: 0,
                stylingOptions: {} as DatasetStylingOptions,
                thematicMapSettings: {} as ThematicMapSettings,
                markerVisualizationMode:
                    MapMarkerVisualizationMode.MARKER_PINS_ONLY,
                application: MaptycsApplication.LOCATIONS,
                isGeocoded: false,
                type: DatasetType.GLOBAL_OVERLAY,
                showIn: ShowInMap.Filter,
            },
            {
                id: "aeris_hail_storm",
                version: 1,
                name: "Active Hail Storm",
                geometryType: DatasetGeometryType.COMPLEX,
                geometryDatasets: [],
                accountID: this.account.id,
                mainSummaryFieldIndex: null,
                fields: [],
                isGeoserver: false,
                geoWorkspace: "",
                isRefreshable: true,
                isLinkable: true,
                hasHazardHubEnabled: false,
                lastRefreshDate: Date.now(),
                overlayGroupId: 0,
                stylingOptions: {} as DatasetStylingOptions,
                thematicMapSettings: {} as ThematicMapSettings,
                markerVisualizationMode:
                    MapMarkerVisualizationMode.MARKER_PINS_ONLY,
                application: MaptycsApplication.LOCATIONS,
                isGeocoded: false,
                type: DatasetType.GLOBAL_OVERLAY,
                showIn: ShowInMap.Filter,
            },
        ];

        // Set eligibleOverlays to only contain our hardcoded options
        this.eligibleOverlays = hardcodedOverlays;
    }

    /**
     * Fetches all account members' emails for the current account
     */
    fetchAccountMembers(accountId: number = this.account.id) {
        this.membersService.getEmailsFromAccountMembers(accountId)
            .subscribe({
                next: (emails) => {
                    this.accountMemberEmails = emails || [];

                    // Enhanced logging for email addresses
                    console.log(`Found ${this.accountMemberEmails.length} account member emails for account ID ${accountId}:`);
                    this.accountMemberEmails.forEach((email, index) => {
                    });
                },
            });
    }

    fetchFormulas() {
        this.workspaceItemService
            .getWorkspaceItemsForMember(
                this.dataset.id,
                WorkspaceItemType.FORMULA
            )
            .subscribe(
                (items) => {
                    this.formulas = items.map(item => ({
                        ...item,
                        name: decodeHtmlText(item.name)
                    }));
                },
                (error) => {
                    console.error("Error fetching formulas:", error);
                }
            );
    }

    addNotification() {
        if (!this.showPanel) {
            this.notifService.error("At least one group should be default");
            return;
        }
        this.notifications.push({
            datasetId: this.dataset.id,
            locationBreakdownFieldId: "",
            memberId: this.accountMember.id,
            notificationName: "",
            overlayId: "",
            currency: Constants.DEFAULT_CURRENCY,
            radius: Constants.DEFAULT_RADIUS,
            distanceUnit: DistanceUnit.KM,
            ccEmails: [],
            personalizedSubject: "",
            personalizedMessage: "",
            sendOneAlertDaily: true,
        });
    }

    deleteEventNotif(notif: EventNotificationSettings) {
        let index = this.notifications.indexOf(notif);
        this.notifications.splice(index, 1);
    }
}
